import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { useTransportDataPartners } from "src/data-partners/useDataPartner";
import color from "src/design-system/tokens/color";
import { spacing } from "src/design-system/tokens/spacing";
import { useIsHotelsUrlDeeplink } from "src/utils/hooks/useNavigateToHotelsPage";
import { useScreenMinWidth } from "src/utils/hooks/useScreenMinWidth";
import { navigateToNewStateHash } from "src/utils/location/navigateToNewStateHash";
import { getUidCookie } from "src/utils/uid";
import styled, { css } from "styled-components";
import { border_radius } from "src/design-system/tokens/border";
import {
  HotelPromo,
  HotelPromoLoadingSkeleton,
} from "src/domain/SearchResultsScreen/SearchPromos/HotelPromo/HotelPromo";
import { useHotelList } from "src/utils/hooks/useHotelList";
import { CarPromo } from "src/domain/SearchResultsScreen/SearchPromos/CarPromo/CarPromo";
import { tw } from "src/design-system/tw";
import { useHotjar } from "src/utils/hooks/useHotjar";
import { searchResultScreenComponentList } from "../../analytics/generateScreenComponentsRendered/searchResultScreenComponentList";
import { DefaultErrorBoundary } from "../../components/DefaultErrorBoundary/DefaultErrorBoundary";
import { screenMinWidth } from "../../theme";
import { useAnalyticsPageView } from "../../utils/hooks/useAnalyticsPageView";
import useSearch from "../../utils/hooks/useSearch";
import { useTypedLocation } from "../../utils/hooks/useTypedLocation";
import { useTripPlannerContext } from "../TripPlanner/hooks/useTripPlannerContext";
import { updateTripPlanningState } from "../TripPlanner/hooks/useTripPlanningState";
import { TripNavbar } from "../TripPlanner/TripNavbar/TripNavbar";
import { useHotelsContext } from "../HotelsScreen/HotelsContext";
import { SearchBar } from "./SearchBar/SearchBar";
import { UkraineSearchNotification } from "./SearchNotification/UkraineSearchNotification";
import {
  type Errors,
  SearchResultError,
} from "./SearchResultError/SearchResultError";
import { SearchResultsContent } from "./SearchResultsContent/SearchResultsContent";
import { SearchResultsLoading } from "./SearchResultsLoading/SearchResultsLoading";
import messages from "./SearchResultsScreen.messages";
import { useEmulateReload } from "./useEmulateReload";
import { PromoContainer } from "./SearchPromos/SearchPromoContainer";

type Props = {
  context: "transport" | "tripPlanner";
};

export function SearchResultsScreen(props: Props) {
  const location = useTypedLocation();
  const navigate = useNavigate();
  const { isMobile } = useScreenMinWidth();
  const intl = useIntl();
  const uid = getUidCookie();
  const isHotelsDeeplink = useIsHotelsUrlDeeplink();
  const { searchResponse } = useSearch();
  const { emulateReload } = useEmulateReload();
  const { destination: hotelsDestination } = useHotelsContext();
  const components = searchResultScreenComponentList(searchResponse);
  const { hotelList } = useHotelList("static");

  useHotjar({
    id: "searchResults",
    isEnabled: searchResponse?.routes?.length === 1,
    isFiredOnLoad: true,
  });

  useTransportDataPartners(
    searchResponse,
    searchResponse?.analytics?.interest_data,
    uid,
    undefined,
    undefined,
    undefined,
    undefined,
    true, // isSearchScreen.
  );

  useAnalyticsPageView(
    {
      pagePath: location.pathname + location.search + location.hash,
      pageLocation: window.location.href,
    },
    searchResponse,
    "SearchResults",
    undefined,
    components,
  );

  const { tripPlannerDetails, dispatch, tripPlanningState } =
    useTripPlannerContext();

  function handleTripSave(routeIndex?: string) {
    if (searchResponse) {
      dispatch({
        type: "SAVE_SEARCH",
        searchResponse,
        url: {
          pathname: location.pathname,
          hash: `#trips/r/${
            searchResponse.routes[Number(routeIndex)].canonicalName
          }`,
        },
        routeIndex: routeIndex ? Number(routeIndex) : undefined,
      });

      updateTripPlanningState(
        tripPlanningState,
        tripPlannerDetails,
        searchResponse,
        undefined,
        undefined,
        "Search",
      );
    }
  }

  function navigateToTripsScreen() {
    navigateToNewStateHash(navigate, {}, "#trips", {
      ...location,
    });
  }

  return (
    <Wrapper>
      {props.context === "tripPlanner" && (
        <TripNavbar
          variant={isMobile ? "default" : "fullScreen"}
          showSearchBreadcrumb
          onBackClick={isMobile ? undefined : navigateToTripsScreen}
          headingText={intl.formatMessage(messages.travelOptions)}
        />
      )}
      {props.context === "transport" && isMobile && (
        <SearchBarWrapper>
          <SearchBar />
        </SearchBarWrapper>
      )}
      <Content $isTripsDrawer={props.context === "tripPlanner"}>
        {components.includes("SearchNotification") && (
          <UkraineSearchNotification />
        )}
        {components.includes("RouteList") && (
          <DefaultErrorBoundary defaultMessage>
            <RouteListWrapper $isTripsDrawer={props.context === "tripPlanner"}>
              <RouteList
                context={props.context}
                isLoading={emulateReload}
                handleTripSave={handleTripSave}
                isHotelsScreen={isHotelsDeeplink}
              />
            </RouteListWrapper>
          </DefaultErrorBoundary>
        )}
      </Content>
      {props.context === "transport" && searchResponse && (
        <div
          className={tw(
            "flex flex-col gap-3 bg-border py-3 md:bg-transparent md:pt-0 md:pb-6",
          )}
        >
          {components.includes("HotelCta") &&
            hotelsDestination &&
            (!hotelList ? (
              <PromoContainer>
                <HotelPromoLoadingSkeleton />
              </PromoContainer>
            ) : (
              hotelList.length > 0 && (
                <HotelPromo
                  destination={hotelsDestination}
                  hotelList={hotelList}
                />
              )
            ))}
          {components.includes("RentalCarCta") && (
            <CarPromo
              searchResponse={searchResponse}
              eventCategory="SearchResults"
            />
          )}
        </div>
      )}
    </Wrapper>
  );
}

function RouteList(props: {
  context: "transport" | "tripPlanner";
  isLoading: boolean;
  handleTripSave: (routeIndex?: string) => void;
  isHotelsScreen?: boolean;
}) {
  const { origin, destination, searchResponse, isLoading, error } = useSearch();
  const searchResponsePending = props.isLoading || isLoading;
  const hasResults = !!searchResponse?.routes?.length;

  if (error) {
    return <ErrorContent errorType="error" />;
  } else if (searchResponse && !hasResults) {
    return <ErrorContent errorType="no-results" />;
  } else if (!origin) {
    return <ErrorContent errorType="no-origin" />;
  } else if (!destination) {
    return <ErrorContent errorType="no-destination" />;
  } else if (searchResponsePending) {
    return <SearchResultsLoading />;
  } else if (searchResponse) {
    return (
      <SearchResultsContent
        context={props.context}
        searchResponse={searchResponse}
        handleTripSave={props.handleTripSave}
      />
    );
  } else {
    return null;
  }
}

function ErrorContent(props: { errorType: Errors }) {
  return (
    <ErrorComponent>
      <SearchResultError errorType={props.errorType} />
    </ErrorComponent>
  );
}

const Wrapper = styled.div`
  position: relative;
  display: inline;
`;

const ErrorComponent = styled.div`
  padding: ${spacing.xl};
`;

const Content = styled.div<{
  $isTripsDrawer?: boolean;
}>`
  ${({ $isTripsDrawer }) =>
    !$isTripsDrawer
      ? css`
          background-color: ${color.primitive.neutral100};
          padding: ${spacing.xl} ${spacing.xl} ${spacing.xxl};

          ${screenMinWidth.sm} {
            background-color: ${color.bg.surface.secondaryActive};
            padding: unset;
            padding-bottom: ${spacing.xxl};
          }
        `
      : css`
          background-color: ${color.bg.fill.solidHover};
          padding: ${spacing.md} ${spacing.md} ${spacing.xxl} ${spacing.md};

          ${screenMinWidth.sm} {
            background-color: ${color.bg.fill.solidHover};
            padding: ${spacing.xxl} ${spacing.xl} ${spacing.xl};
          }
        `}
`;

const RouteListWrapper = styled.div<{
  $isTripsDrawer?: boolean;
}>`
  ${({ $isTripsDrawer }) =>
    !$isTripsDrawer &&
    css`
      ${screenMinWidth.sm} {
        padding: ${spacing.xxl} ${spacing.xl} ${spacing.xl};
        background-color: ${color.bg.surface.surface};
        border-radius: ${border_radius.rounded_lg};
      }
    `}
`;

const SearchBarWrapper = styled.header`
  position: sticky;
  left: 0;
  top: 0;
  right: 0;
  z-index: 50;
`;
